/**
 * Dominio de Equipo, Invitaciones y Perfil (Fase 1).
 *
 * Tipos alineados 1:1 al contrato de la API + hooks de TanStack Query.
 * Todo lo operativo (GET /team, invitar, revocar) filtra por empresa en el
 * backend (multi-tenant); acá sólo consumimos el contrato.
 *
 * El alta por invitación (accept) es pública y deja al usuario logueado:
 * reusa `establishSession` del AuthProvider (mismo shape que register).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';
import { useAuth } from './auth/AuthProvider';
import type { RegisterResponse, User, UserRole } from './auth/types';

// --- Roles / gating ---------------------------------------------------------

/** Rol asignable al invitar dentro de una empresa. */
export type TeamRole = 'COMPANY_ADMIN' | 'MEMBER';

/** ¿El rol puede administrar el equipo (ver /equipo, invitar, revocar)? */
export function isCompanyAdmin(role: UserRole | undefined | null): boolean {
  return role === 'COMPANY_ADMIN' || role === 'SUPER_ADMIN';
}

/** ¿Es super admin de la plataforma? (accede al panel /admin). */
export function isSuperAdmin(role: UserRole | undefined | null): boolean {
  return role === 'SUPER_ADMIN';
}

// --- Tipos de dominio -------------------------------------------------------

export interface TeamMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  job_title: string | null;
  role: UserRole;
  status: string;
  avatar_path: string | null;
  /** Teléfono de contacto; puede faltar según el endpoint que hidrata al miembro. */
  phone?: string | null;
}

export interface TeamInvitation {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  role: UserRole;
  status: string;
  invite_url: string;
  created_at: string;
  expires_at: string;
}

export interface TeamResponse {
  members: TeamMember[];
  invitations: TeamInvitation[];
}

// --- Keys de cache ----------------------------------------------------------

export const teamKey = ['team'] as const;
export const invitationKey = (token: string) => ['invitation', token] as const;

// --- Payloads / respuestas --------------------------------------------------

export interface InvitePayload {
  email: string;
  first_name?: string;
  last_name?: string;
  job_title?: string;
  role: TeamRole;
}

export interface InviteResponse {
  id: string;
  email: string;
  role: UserRole;
  status: string;
  /** Link listo para compartir (por si no hay email configurado). */
  invite_url: string;
  expires_at: string;
}

export interface UpdateProfilePayload {
  first_name?: string;
  last_name?: string;
  job_title?: string;
  department?: string;
  national_id?: string;
  phone?: string;
  locale?: string;
  timezone?: string;
  /** Página de inicio preferida (migr. 040). */
  home_page?: 'inicio' | 'proyectos' | 'reportes';
  /** Mostrar/ocultar el acordeón guía en /proyectos. */
  show_projects_guide?: boolean;
}

/** Datos públicos de una invitación (para precargar la pantalla de alta). */
export interface InvitationInfo {
  email: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  role: UserRole;
  company_name: string;
  valid: true;
}

export interface AcceptInvitationPayload {
  first_name: string;
  last_name: string;
  password: string;
  job_title?: string;
  phone?: string;
}

/**
 * Respuesta de aceptar invitación cuando el email YA tiene cuenta y el request
 * está autenticado como ese usuario: activa la membresía SIN emitir tokens (la
 * sesión actual sigue vigente). Trae la empresa a la que se sumó.
 */
export interface AcceptJoinedResponse {
  ok: true;
  company: { id: string; name: string };
}

/**
 * Aceptar puede devolver:
 *  - una sesión completa (usuario NUEVO → auto-login), o
 *  - `{ok:true, company}` (usuario existente ya logueado → sólo se activó la
 *    membresía; la página refresca /auth/me y ofrece cambiar de empresa).
 * El 401 `needs_login` (email con cuenta, request no autenticado como ese
 * usuario) llega como error, no acá.
 */
export type AcceptInvitationResponse = RegisterResponse | AcceptJoinedResponse;

// --- Hooks: equipo (protegidos) ---------------------------------------------

export function useTeam() {
  return useQuery({
    queryKey: teamKey,
    queryFn: () => api.get<TeamResponse>('/team'),
    // No reintentar ante error/403: quien no administra el equipo no debe
    // martillar el endpoint (la home lo consume de forma tolerante).
    retry: false,
  });
}

export function useInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: InvitePayload) => api.post<InviteResponse>('/invitations', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKey });
    },
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/invitations/${id}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKey });
    },
  });
}

/** Quita a un miembro ACTIVE de la empresa actual (no borra su cuenta global). */
export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete<unknown>(`/team/members/${userId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKey });
    },
  });
}

/**
 * Cambia el rol (COMPANY_ADMIN <-> MEMBER) de un miembro ACTIVE. El nuevo rol lo
 * toma el usuario afectado en su próximo refresh de sesión (no se re-loguea).
 */
export function useChangeMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TeamRole }) =>
      api.patch<{ user_id: string; role: UserRole }>(`/team/members/${userId}/role`, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKey });
    },
  });
}

export function useUpdateProfile() {
  const { updateUser } = useAuth();
  return useMutation({
    // El contrato devuelve el user actualizado; toleramos { user } por robustez.
    mutationFn: async (payload: UpdateProfilePayload) => {
      const res = await api.patch<User | { user: User }>('/me', payload);
      return 'user' in (res as { user?: User }) ? (res as { user: User }).user : (res as User);
    },
    onSuccess: (user) => {
      updateUser(user);
    },
  });
}

/** Cambia la contraseña propia (contraseña actual + nueva). */
export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: ChangePasswordPayload) =>
      api.post<{ changed: boolean }>('/me/password', payload),
  });
}

/**
 * Borra la PROPIA empresa (irreversible). Sólo COMPANY_ADMIN. Requiere re-auth
 * por contraseña. El backend responde 204; el llamador hace logout tras el éxito
 * (la sesión ya quedó inválida porque el usuario fue borrado en la cascada).
 */
export function useDeleteMyCompany() {
  return useMutation({
    mutationFn: (password: string) =>
      api.post<null>('/me/company/delete', { password }),
  });
}

/**
 * Sube/cambia la foto de perfil (multipart, campo `avatar`, imagen ≤5MB).
 * Al éxito fusiona `avatar_path` en el `user` en memoria para que el nuevo
 * avatar aparezca de inmediato en la topbar y demás lugares.
 */
export function useUploadAvatar() {
  const { updateUser, user } = useAuth();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      // El backend (AttachmentController::avatar -> Upload::accept) espera el
      // archivo bajo el campo `file`, no `avatar`.
      form.append('file', file);
      return api.post<{ avatar_path: string }>('/me/avatar', form);
    },
    onSuccess: ({ avatar_path }) => {
      if (user) updateUser({ ...user, avatar_path });
    },
  });
}

// --- Hooks: invitación pública (sin auth) -----------------------------------

export function useInvitation(token: string) {
  return useQuery({
    queryKey: invitationKey(token),
    queryFn: () =>
      api.get<InvitationInfo>(`/invitations/${encodeURIComponent(token)}`, {
        skipAuthRefresh: true,
      }),
    enabled: Boolean(token),
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Acepta la invitación. Dos caminos según el email:
 *  - Usuario NUEVO: la respuesta trae una sesión completa (token+user+company +
 *    cookie de refresh httpOnly) → auto-login vía `establishSession`.
 *  - Usuario EXISTENTE ya logueado como ese email: `{ok:true, company}` sin
 *    tokens → NO tocamos la sesión; la página refresca /auth/me y ofrece cambiar
 *    a la empresa nueva.
 * El payload es opcional (el caso autenticado no necesita nombre/contraseña).
 */
export function useAcceptInvitation(token: string) {
  const { establishSession } = useAuth();
  return useMutation({
    mutationFn: (payload: Partial<AcceptInvitationPayload>) =>
      api.post<AcceptInvitationResponse>(
        `/invitations/${encodeURIComponent(token)}/accept`,
        payload,
        { skipAuthRefresh: true },
      ),
    onSuccess: (res) => {
      // Sólo hay auto-login cuando vino una sesión (usuario nuevo). El caso
      // `{ok:true}` lo maneja la página (reload + switch), sin tocar la sesión.
      if ('access_token' in res) establishSession(res);
    },
  });
}
